import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { createQueryString } from '../../../utilities/utilities';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import './devex_async_typeahead_custom_styles.scss';

/*
  See https://github.com/ericgio/react-bootstrap-typeahead/tree/master/docs
  for full reference of options
*/
const AutocompleteBootstrap5 = ({
  placeholder = 'Enter search',
  selected = [],
  filter = {},
  minLength = 0,
  searchOnMount = false,
  onlySearchOnMount = false,
  dataResponse = 'data',
  arrayFormat = 'brackets',
  labelKey = 'text',
  filterResults = (results) => results,
  results: initialResults = [],
  isInvalid = false,
  invalidFeedback = '',
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(initialResults);

  /**
   * On search, run API request to the BE when it applies.
   *
   * @param {string} q - User query input
   *
   * @returns {undefined}
   */
  const onSearch = (q = '') => {
    if (onlySearchOnMount && results.length) return;

    setIsLoading(true);
    fetch(props.apiPath + createQueryString({ ...filter, q }, arrayFormat))
      .then((response) => response.json())
      .then((response) => {
        setResults(filterResults(response[dataResponse]));
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const onChange = (event) => {
    if (props.multiple) {
      return props.onSelect(event);
    }

    const isValid = event.length;
    props.onSelect(isValid ? event[0] : undefined);
  };

  useEffect(() => {
    if (searchOnMount || onlySearchOnMount) {
      onSearch();
    }
  }, []);

  // Set default autocomplete results
  useEffect(() => {
    if (!isLoading && props.results) {
      setResults(props.results);
    }
  }, [props.results]);

  const config = {
    onSearch,
    onChange,
    isLoading,
    options: results,
    clearButton: true,
    highlightOnlyResult: true,
    caseSensitive: false,
    placeholder,
    labelKey,
    useCache: false,
    selected,
    minLength,
    isInvalid,
    invalidFeedback,
    ...props,
  };

  config.className = 'devex-async-typeahead-c0185393-5bb2-4496-9249-cacdc3811c5f';

  return (
    <>
      <AsyncTypeahead {...config} />
      { config.isInvalid && config.invalidFeedback && (
        <div className='invalid-feedback'>
          <span className='fal small icon-times me-2' />
          {config.invalidFeedback}
        </div>
      )}
    </>
  );
};

AutocompleteBootstrap5.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  placeholder: PropTypes.string,
  selected: PropTypes.array,
  apiPath: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  searchOnMount: PropTypes.bool,
  onlySearchOnMount: PropTypes.bool,
  multiple: PropTypes.bool,
  minLength: PropTypes.number,
  filter: PropTypes.object,
  dataResponse: PropTypes.string,
  arrayFormat: PropTypes.string,
  filterBy: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
  allowNew: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  highlightOnlyResult: PropTypes.bool,
  newSelectionPrefix: PropTypes.string,
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  inputProps: PropTypes.object,
  clearButton: PropTypes.bool,
  renderInput: PropTypes.func,
  onBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  delay: PropTypes.number,
  renderMenuItemChildren: PropTypes.func,
  filterResults: PropTypes.func,
  results: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]),
  disabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  invalidFeedback: PropTypes.string,
};

export default AutocompleteBootstrap5;
